import { flattenBoard } from './conservation.ts';
import type { BoardSnapshot, Meld, Tile } from './types.ts';
import { INITIAL_MELD_THRESHOLD } from './types.ts';
import { validateMeld } from './validation.ts';

/**
 * 找出本回合玩家「新增」的 meld：
 * - 桌面上原本不存在的組合（整組皆為本回合新放上桌的牌）。
 * - 若一個 meld 中混合了既有桌牌與新牌 → 屬於 manipulation，破冰階段視為無效新增（0 分）。
 */
export function diffNewMelds(
  currentBoard: readonly Meld[],
  initial: BoardSnapshot,
): Meld[] {
  const initialIds = new Set(flattenBoard(initial.board).map((t) => t.id));
  const newMelds: Meld[] = [];
  for (const meld of currentBoard) {
    if (meld.every((t) => !initialIds.has(t.id))) {
      newMelds.push(meld);
    }
  }
  return newMelds;
}

export interface InitialMeldCheck {
  /** 本回合新組合的加總分數 */
  score: number;
  /** 是否達門檻 ≥ 30 */
  meetsThreshold: boolean;
  /** 任一新組合不合法則為 false */
  allValid: boolean;
  newMelds: Meld[];
}

/**
 * 破冰檢查：加總本回合所有新增的合法組合分數。
 * allowJoker 為 true 時百搭以被替代牌面值計入；false 時百搭計 0 分。
 */
export function checkInitialMeld(
  currentBoard: readonly Meld[],
  initial: BoardSnapshot,
  allowJoker: boolean,
): InitialMeldCheck {
  const newMelds = diffNewMelds(currentBoard, initial);
  let score = 0;
  let allValid = true;
  for (const m of newMelds) {
    const v = validateMeld(m);
    if (!v.valid) {
      allValid = false;
      continue;
    }
    if (allowJoker) {
      score += v.score;
    } else {
      // 嚴格變體：每張百搭在本組中對分數的貢獻扣除
      // 做法：用非百搭部分重新估算該組貢獻。
      score += scoreExcludingJokers(m, v.kind!);
    }
  }
  return {
    score,
    meetsThreshold: score >= INITIAL_MELD_THRESHOLD,
    allValid,
    newMelds,
  };
}

function scoreExcludingJokers(meld: Meld, kind: 'group' | 'run'): number {
  if (kind === 'group') {
    // group 每張計同一數字，非百搭部分直接加總即可
    return meld.filter((t) => !t.isJoker).reduce((s, t) => s + (t.number ?? 0), 0);
  }
  // run: 先鎖定 start（最小 non-joker 之數字減掉其在序列中的位置）
  // 簡化：找第一張非 joker 的 index 與 number 推算 start，若無（理論上不會發生）fallback 為 1。
  let start = 1;
  for (let i = 0; i < meld.length; i++) {
    const t = meld[i]!;
    if (!t.isJoker && t.number != null) {
      start = t.number - i;
      break;
    }
  }
  let s = 0;
  for (let i = 0; i < meld.length; i++) {
    const t = meld[i]!;
    if (!t.isJoker) s += start + i;
  }
  return s;
}

export function hasUnusedTakenBackJokers(rack: readonly Tile[], takenBackIds: readonly string[]): boolean {
  const rackIds = new Set(rack.map((t) => t.id));
  return takenBackIds.some((id) => rackIds.has(id));
}

/**
 * 破冰階段防重組：將 currentBoard 扣除 newMelds 後，剩餘的組合必須與 initial.board「完全一致」。
 * 完全一致 = 同樣數量的 meld、每個 meld 的 tile id 集合相同（顏色 / 順序視為 meld 本身定義）。
 * 任何對原 meld 的拆分、合併、加牌都會導致不一致 → 判定破冰階段非法重組。
 */
export function isBoardUntouchedExceptNewMelds(
  currentBoard: readonly Meld[],
  newMelds: readonly Meld[],
  initial: BoardSnapshot,
): boolean {
  const newMeldSignatures = new Set(newMelds.map(signatureOf));
  const remaining = currentBoard.filter((m) => !newMeldSignatures.has(signatureOf(m)));
  if (remaining.length !== initial.board.length) return false;
  const remainingSigs = remaining.map(signatureOf).sort();
  const initialSigs = initial.board.map(signatureOf).sort();
  for (let i = 0; i < remainingSigs.length; i++) {
    if (remainingSigs[i] !== initialSigs[i]) return false;
  }
  return true;
}

function signatureOf(meld: Meld): string {
  return meld
    .map((t) => t.id)
    .sort()
    .join('|');
}
